import { useTranslate } from '@refinedev/core';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  InputNumber,
  Radio,
  Row,
  Select,
  TreeSelect,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { type Dayjs } from 'dayjs';
import {
  ContractRateKind,
  CycleKind,
  PaymentMoment,
  RateVariant,
} from '@/api/generated/schemas';
import type { PublisherTreeDataNode } from '@/components/user_catalog/publishers/publisherTree';
import {
  CONTRACT_STARTER_RATE,
  isDisabledStartDate,
} from './contractFormValues';

interface ContractFormFieldsProps {
  publisherTreeData: PublisherTreeDataNode[];
  publisherDisabled?: boolean;
}

/** Contract fields shared by the module create/edit pages and the
 * organization-show modal. Renders `Form.Item`s only — the caller owns the
 * surrounding `<Form>` and the submit mapping via `toContractPayload`. */
export const ContractFormFields = ({
  publisherTreeData,
  publisherDisabled,
}: ContractFormFieldsProps) => {
  const t = useTranslate();
  const tk = (suffix: string) => t(`tenancy.contracts.${suffix}`);
  const form = Form.useFormInstance();

  return (
    <>
      <Form.Item
        name="publisher_id"
        label={tk('fields.publisher')}
        extra={tk('form.publisher_hint')}
      >
        {/* Single-select: a contract targets one publisher, and clearing it
            makes the contract the organization-wide default. */}
        <TreeSelect
          allowClear
          disabled={publisherDisabled}
          treeData={publisherTreeData}
          treeDefaultExpandAll
          showSearch
          treeNodeFilterProp="title"
          placeholder={tk('publisher_default')}
        />
      </Form.Item>

      <Form.Item
        name="valid_from"
        label={tk('fields.valid_from')}
        extra={tk('form.valid_from_hint')}
        rules={[
          { required: true },
          {
            // `disabledDate` alone doesn't invalidate a prefilled past
            // value on edit.
            validator: (_, value: Dayjs | undefined) =>
              value && isDisabledStartDate(value)
                ? Promise.reject(new Error(tk('form.valid_from_past')))
                : Promise.resolve(),
          },
        ]}
      >
        <DatePicker
          format="DD.MM.YYYY"
          style={{ width: '100%' }}
          disabledDate={isDisabledStartDate}
        />
      </Form.Item>

      <Form.Item
        label={tk('fields.rates')}
        required
        style={{ marginBottom: 0 }}
      >
        <Form.List
          name="rates"
          rules={[
            {
              validator: (_, rates?: { is_default?: boolean }[]) => {
                if (!rates?.length) {
                  return Promise.reject(new Error(tk('form.rates_required')));
                }
                // Mirrors the backend rule: exactly one default rate.
                return rates.filter((rate) => rate.is_default).length === 1
                  ? Promise.resolve()
                  : Promise.reject(new Error(tk('form.default_required')));
              },
            },
          ]}
        >
          {(fields, { add, remove }, { errors }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Card
                  key={key}
                  size="small"
                  style={{ marginBottom: 8 }}
                  title={
                    <Form.Item
                      name={[name, 'is_default']}
                      valuePropName="checked"
                      noStyle
                    >
                      <Radio
                        onChange={() =>
                          // Radio semantics: checking one clears all others,
                          // so at most one default exists; "at least one" is
                          // enforced by the list validator.
                          fields.forEach((field) =>
                            form.setFieldValue(
                              ['rates', field.name, 'is_default'],
                              field.name === name
                            )
                          )
                        }
                      >
                        {tk('fields.is_default')}
                      </Radio>
                    </Form.Item>
                  }
                  extra={
                    fields.length > 1 && (
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    )
                  }
                >
                  {/* Five fields per row: the 24-column grid has no exact
                      fifth, so the columns flex to equal widths instead. */}
                  <Row gutter={8}>
                    <Col flex="1 1 0" style={{ minWidth: 0 }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'variant']}
                        label={tk('fields.rate_variant')}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 8 }}
                      >
                        <Select
                          options={Object.values(RateVariant).map((value) => ({
                            label: tk(`rate_variant.${value}`),
                            value,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col flex="1 1 0" style={{ minWidth: 0 }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'kind']}
                        label={tk('fields.rate_kind')}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 8 }}
                      >
                        <Select
                          options={Object.values(ContractRateKind).map(
                            (value) => ({
                              label: tk(`rate_kind.${value}`),
                              value,
                            })
                          )}
                        />
                      </Form.Item>
                    </Col>
                    <Col flex="1 1 0" style={{ minWidth: 0 }}>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, cur) =>
                          prev.rates?.[name]?.kind !== cur.rates?.[name]?.kind
                        }
                      >
                        {({ getFieldValue }) => {
                          const exclusive =
                            getFieldValue(['rates', name, 'kind']) ===
                            ContractRateKind.exclusive;
                          return (
                            <Form.Item
                              {...restField}
                              name={[name, 'exclusive_period_months']}
                              label={tk('fields.exclusive_period_months')}
                              rules={[
                                { required: true },
                                // The backend rejects an exclusive rate whose
                                // window is 0.
                                {
                                  type: 'number',
                                  min: exclusive ? 1 : 0,
                                },
                              ]}
                              style={{ marginBottom: 8 }}
                            >
                              <InputNumber
                                min={exclusive ? 1 : 0}
                                precision={0}
                                disabled={!exclusive}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Col>
                    <Col flex="1 1 0" style={{ minWidth: 0 }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'payment_moment']}
                        label={tk('fields.payment_moment')}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 8 }}
                      >
                        <Select
                          options={Object.values(PaymentMoment).map(
                            (value) => ({
                              label: tk(`payment_moment.${value}`),
                              value,
                            })
                          )}
                        />
                      </Form.Item>
                    </Col>
                    <Col flex="1 1 0" style={{ minWidth: 0 }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'value']}
                        label={tk('fields.value')}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 8 }}
                      >
                        <InputNumber
                          // stringMode keeps the decimal a string end-to-end —
                          // the API stores decimals as strings.
                          stringMode
                          step="0.01"
                          min="0"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={8}>
                    <Col flex="1 1 0" style={{ minWidth: 0 }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'min_consumption_threshold']}
                        label={tk('fields.min_consumption_threshold')}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 8 }}
                      >
                        <InputNumber
                          stringMode
                          step="0.01"
                          min="0"
                          max="1"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col flex="1 1 0" style={{ minWidth: 0 }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'max_consumption_threshold']}
                        label={tk('fields.max_consumption_threshold')}
                        dependencies={[
                          ['rates', name, 'min_consumption_threshold'],
                        ]}
                        rules={[
                          { required: true },
                          ({ getFieldValue }) => ({
                            validator: (_, value: string | undefined) => {
                              const min = getFieldValue([
                                'rates',
                                name,
                                'min_consumption_threshold',
                              ]);
                              return value != null &&
                                min != null &&
                                Number(value) <= Number(min)
                                ? Promise.reject(
                                    new Error(tk('form.threshold_order'))
                                  )
                                : Promise.resolve();
                            },
                          }),
                        ]}
                        style={{ marginBottom: 8 }}
                      >
                        <InputNumber
                          stringMode
                          step="0.01"
                          min="0"
                          max="1"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                    <Col flex="1 1 0" style={{ minWidth: 0 }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'cycle_kind']}
                        label={tk('fields.cycle_kind')}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 8 }}
                      >
                        <Select
                          options={Object.values(CycleKind).map((value) => ({
                            label: tk(`cycle_kind.${value}`),
                            value,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col flex="1 1 0" style={{ minWidth: 0 }}>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, cur) =>
                          prev.rates?.[name]?.cycle_kind !==
                          cur.rates?.[name]?.cycle_kind
                        }
                      >
                        {({ getFieldValue }) => {
                          const rolling =
                            getFieldValue(['rates', name, 'cycle_kind']) ===
                            CycleKind.rolling;
                          return (
                            <Form.Item
                              {...restField}
                              name={[name, 'cycle_length_days']}
                              label={tk('fields.cycle_length_days')}
                              rules={[{ required: rolling }]}
                              style={{ marginBottom: 8 }}
                            >
                              <InputNumber
                                min={1}
                                precision={0}
                                disabled={!rolling}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Col>
                    <Col flex="1 1 0" style={{ minWidth: 0 }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'max_billable_cycles']}
                        label={tk('fields.max_billable_cycles')}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 8 }}
                      >
                        <InputNumber
                          min={1}
                          precision={0}
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  onClick={() =>
                    add({ ...CONTRACT_STARTER_RATE, is_default: false })
                  }
                >
                  {tk('form.add_rate')}
                </Button>
                <Form.ErrorList errors={errors} />
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form.Item>
    </>
  );
};
